import type { Theme } from '@material-ui/core';
import { CircularProgress, createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import { useBorerHeartbeat } from '@/components/BorerHeartbeat/useBorerHeartbeat';
import { useEventTracking } from '@/utilities/hooks/useEventTracking';

import { createGoal } from '../actions/goals/Goal';
import { kind as logOut } from '../actions/goals/LogOut';
import { dataManagerContext } from '../dataManagerContext';
import { logError } from '../logError';
import { useReplication } from '../useReplication';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.background.paper,
      height: '100vh',
    },
  }),
);

export const Logout: React.FC = () => {
  const { state, dispatch } = useContext(dataManagerContext);
  const classes = useStyles();

  const [statusText, setStatusText] = useState('Logging Out...');

  useEffect(() => {
    const goal = createGoal(logOut);
    goal.resolvers.promise
      .then(() => {
        window.location.replace('/login'); // dont use history in this case a trigger a page refresh
      })
      .catch(error => {
        logError('Error during log out', error, {});
        setStatusText(`Error during log out: ${error}`);
      });

    dispatch(goal);
  }, [dispatch]);

  useEffect(() => {
    if (state.replication) {
      setStatusText('Stopping sync...');
      return;
    }

    if (state.database) {
      setStatusText('Cleaning up... 🧹');
      return;
    }
  }, [state]);

  const replication = useReplication();
  const { sendBorerHeartbeat } = useBorerHeartbeat();

  useEffect(() => {
    if (!replication) {
      const lastHearbeat = dayjs();
      sendBorerHeartbeat({
        lastUserInteractionTime: lastHearbeat,
        logoutTime: lastHearbeat,
      }).catch(reason => {
        logError('Error sending final hearbeat events on logout', reason, {
          lastHearbeat: lastHearbeat.toISOString(),
        });
      });
    }
  }, [replication, sendBorerHeartbeat]);

  const { sendCachedEvents } = useEventTracking();
  useEffect(() => {
    if (!replication) {
      sendCachedEvents().catch(reason => {
        logError('Error sending cached events on logout', reason, {});
      });
    }
  }, [replication, sendCachedEvents]);

  return (
    <Grid
      container
      className={classes.root}
      justify="center"
      alignItems="center"
      direction="column"
      spacing={2}
    >
      <Grid item>
        <CircularProgress />
      </Grid>
      <Grid item xs={3}>
        <Typography variant="subtitle2">{statusText}</Typography>
      </Grid>
    </Grid>
  );
};
